/**
 * The portal's three "choose your password" pages share this screen (tracker
 * item 45):
 *   /portal/verify?token=          confirm the email AND choose the password
 *                                  (registration stores none: the pre-hijack fix)
 *   /portal/accept-invite?token=   staff invitation
 *   /portal/reset-password?token=  forgot password
 * Public: no session, no staff chrome. A link that is unknown, spent or
 * expired is one message. A weak password is refused before the link is spent.
 */
import React, { useMemo, useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { createAPIClient, IPortalApi } from '../../api';
import { PortalPublicLayout, portalLinkClass } from './PortalPublicLayout';
import { PortalPasswordFields } from './PortalPasswordFields';
import { InlineAlert } from './PortalStates';
import { primaryButtonClass } from '../../components/formStyles';
import { getPasswordStrengthError } from '../../utils/password';
import { getErrorCode } from '../../utils/apiError';
import { describePortalFailure, passwordsProblem, tokenFromSearch } from '../../utils/portal';

export type SetPasswordMode = 'verify' | 'invite' | 'reset';

const COPY: Record<SetPasswordMode, { title: string; intro: string; submit: string; busy: string; doneTitle: string; done: string }> = {
  verify: {
    title: 'Confirm your email',
    intro: 'Choose a password for your online account to finish signing up.',
    submit: 'Confirm email and set password',
    busy: 'Confirming…',
    doneTitle: 'Your email is confirmed',
    done: "Sign in with the password you just chose. If we can't match you to your customer account automatically, you will see \"We're matching your account\" and our office will review it.",
  },
  invite: {
    title: 'Set your password',
    intro: 'You were invited to the customer portal. Choose a password to turn on your account.',
    submit: 'Set password',
    busy: 'Saving…',
    doneTitle: 'Your account is ready',
    done: 'Sign in with the password you just chose.',
  },
  reset: {
    title: 'Choose a new password',
    intro: 'Choose a new password for your online account.',
    submit: 'Change password',
    busy: 'Saving…',
    doneTitle: 'Your password was changed',
    done: 'Sign in with your new password.',
  },
};

export function PortalSetPasswordPage({ mode }: { mode: SetPasswordMode }) {
  const api = useMemo(() => createAPIClient() as IPortalApi, []);
  const { search } = useLocation();
  const token = tokenFromSearch(search);
  const copy = COPY[mode];
  const [password, setPassword] = useState('');
  const [confirm, setConfirm] = useState('');
  const [busy, setBusy] = useState(false);
  const [problem, setProblem] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [invalidLink, setInvalidLink] = useState(!token);
  const [done, setDone] = useState(false);

  async function submit(event: React.FormEvent) {
    event.preventDefault();
    if (busy) return;
    const found = passwordsProblem(password, confirm, getPasswordStrengthError);
    setProblem(found);
    setError(null);
    if (found) return;
    setBusy(true);
    try {
      const data = { token, password };
      if (mode === 'verify') await api.verifyPortalEmail(data);
      else if (mode === 'invite') await api.acceptPortalInvite(data);
      else await api.confirmPortalPasswordReset(data);
      setPassword('');
      setConfirm('');
      setDone(true);
    } catch (cause) {
      if (getErrorCode(cause) === 'INVALID_TOKEN') setInvalidLink(true);
      else setError(describePortalFailure(cause, 'We could not save your password. Please try again.').message);
    } finally {
      setBusy(false);
    }
  }

  if (done) {
    return (
      <PortalPublicLayout title={copy.doneTitle}>
        <InlineAlert kind="success">{copy.done}</InlineAlert>
        <Link to={mode === 'verify' ? '/portal/login?verified=1' : '/portal/login'} className={`${primaryButtonClass} mt-4 inline-flex w-full items-center justify-center`}>
          Sign in
        </Link>
      </PortalPublicLayout>
    );
  }

  if (invalidLink) {
    return (
      <PortalPublicLayout title="This link is not valid">
        <InlineAlert>This link is not valid or has expired. Links work once and last a limited time.</InlineAlert>
        <div className="mt-4 flex flex-col">
          {mode === 'verify' && <Link to="/portal/register" className={portalLinkClass}>Register again</Link>}
          <Link to="/portal/forgot-password" className={portalLinkClass}>Forgot your password?</Link>
          <Link to="/portal/login" className={portalLinkClass}>Sign in</Link>
        </div>
      </PortalPublicLayout>
    );
  }

  return (
    <PortalPublicLayout title={copy.title} intro={copy.intro}>
      <form onSubmit={(e) => void submit(e)} className="space-y-4" noValidate>
        <PortalPasswordFields password={password} confirm={confirm} onPassword={setPassword} onConfirm={setConfirm} disabled={busy} problem={problem} />
        {error && <InlineAlert>{error}</InlineAlert>}
        <button type="submit" className={`${primaryButtonClass} w-full text-base`} disabled={busy}>{busy ? copy.busy : copy.submit}</button>
      </form>
    </PortalPublicLayout>
  );
}

export const PortalVerifyPage = () => <PortalSetPasswordPage mode="verify" />;
export const PortalAcceptInvitePage = () => <PortalSetPasswordPage mode="invite" />;
export const PortalResetPasswordPage = () => <PortalSetPasswordPage mode="reset" />;
